import React, { useState } from "react";
import type { User } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/Base/Card";
import { EditIcon, SaveIcon } from "lucide-react";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { AddressFormField } from "./AddressFormField";
import { AddressDisplay } from "./AddressDisplay";
import { PhoneDisplay } from "./PhoneDisplay";
import { arrayify } from "@repo/shared/utils";
import { MultiPhoneFormField } from "./MultiPhoneFormField";
import { userRepo } from "@/repositories/userRepo";

const UserCardRow = ({
  leftChild,
  rightChild,
}: {
  leftChild: React.ReactNode;
  rightChild: React.ReactNode;
}) => {
  return (
    <div className="grid grid-cols-3 my-1 items-start">
      <div className="font-bold">{leftChild}</div>
      <div className="text-right col-span-2">{rightChild}</div>
    </div>
  );
};

export const UserCard = ({ user }: { user: User }) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const validationSchema = yup.object({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
  });

  return (
    <Formik
      initialValues={{ ...user }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await userRepo.update(user.id, {
          firstName: values.firstName,
          lastName: values.lastName,
          primaryAddress: values.primaryAddress,
          primaryPhone: values.primaryPhone,
        });
        setShowEditForm(false);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        resetForm,
        errors,
        touched,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-row justify-between">
                {user.firstName + " " + user.lastName}
                <Button
                  size="icon"
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    if (showEditForm) {
                      handleSubmit();
                    } else {
                      setShowEditForm(true);
                    }
                  }}
                >
                  {showEditForm ? <SaveIcon /> : <EditIcon />}
                </Button>
              </CardTitle>
              <CardDescription>User Profile</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-y-4">
                <UserCardRow
                  leftChild="First Name"
                  rightChild={
                    showEditForm ? (
                      <Input
                        id="firstName"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.firstName && touched.firstName}
                        errorMessage={errors.firstName}
                      />
                    ) : (
                      <div>{values.firstName}</div>
                    )
                  }
                />
                <UserCardRow
                  leftChild="Last Name"
                  rightChild={
                    showEditForm ? (
                      <Input
                        id="lastName"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.lastName && touched.lastName}
                        errorMessage={errors.lastName}
                      />
                    ) : (
                      <div>{values.lastName}</div>
                    )
                  }
                />
                <UserCardRow
                  leftChild="Email"
                  rightChild={
                    showEditForm ? (
                      <Input id="" value={values.email} disabled />
                    ) : (
                      <div>{values.email}</div>
                    )
                  }
                />
                <UserCardRow
                  leftChild="Primary Address"
                  rightChild={
                    showEditForm ? (
                      <AddressFormField
                        fieldName="primaryAddress"
                        address={values.primaryAddress}
                        setFieldValue={setFieldValue}
                      />
                    ) : (
                      <AddressDisplay address={values.primaryAddress} />
                    )
                  }
                />
                <UserCardRow
                  leftChild="Primary Phone"
                  rightChild={
                    showEditForm ? (
                      <MultiPhoneFormField
                        fieldName="primaryPhone"
                        phoneNumbers={values.primaryPhone}
                        setFieldValue={setFieldValue}
                      />
                    ) : (
                      arrayify(values.primaryPhone).map((phone, i) => (
                        <PhoneDisplay key={i} phone={phone} />
                      ))
                    )
                  }
                />
              </div>
            </CardContent>
            {showEditForm && (
              <CardFooter className="flex flex-row justify-between">
                <Button
                  variant="destructive"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setShowEditForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </CardFooter>
            )}
          </Card>
        </form>
      )}
    </Formik>
  );
};
